/**
 * Light Board data: the board query (office-scoped, live), frames (with the
 * offline outbox), history, settings and the presence heartbeat.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FrameResultSchema,
  LightboardBoardSchema,
  LightboardHistorySchema,
  LightboardSettingsSchema,
  QuickButtonSchema,
  type FrameResult,
  type LightboardBoard,
  type LightboardFrame,
  type LightboardSettingsPatch,
  type Priority,
} from '@velora/contracts/lightboard';
import { shouldQueue } from '@velora/domain/lightboard';
import { ApiError } from '@velora/sdk';
import { useEffect } from 'react';
import { z } from 'zod';
import { clientFor, useOfficeQuery } from '../../app/shell.js';
import { observeServerTime } from './clock.js';
import { queueFrame } from './outbox.js';

export const BOARD_KEY = 'lightboard';

export function boardKey(names: 'default' | 'initials') {
  return [BOARD_KEY, 'board', names, { live: ['appointment'] }] as const;
}

/** The board for the office selector (one office or All offices). */
export function useBoard(names: 'default' | 'initials') {
  return useOfficeQuery<LightboardBoard>({
    queryKey: boardKey(names),
    queryFn: async (client, signal) => {
      const sentAt = Date.now();
      const board = await client.request(`/lightboard/board?names=${names}`, {
        schema: LightboardBoardSchema,
        signal,
      });
      observeServerTime(board.serverNow, sentAt, Date.now());
      return board;
    },
    // Live events refetch; this only catches what time alone changes (expiry).
    refetchInterval: 60_000,
    staleTime: 5_000,
  });
}

export type SendOutcome = { kind: 'sent'; result: FrameResult } | { kind: 'queued' };

/**
 * Sends one frame to its office. A network failure queues it in the outbox
 * (replayed later with its original time); an HTTP refusal is thrown.
 */
export async function sendFrame(
  locationId: string,
  frame: LightboardFrame,
  priority: Priority,
): Promise<SendOutcome> {
  const stamped = { ...frame, occurredAt: frame.occurredAt ?? new Date().toISOString() };
  try {
    const result = await clientFor(locationId).request('/lightboard/frames', {
      method: 'POST',
      body: stamped,
      schema: FrameResultSchema,
    });
    return { kind: 'sent', result };
  } catch (err) {
    if (shouldQueue(err)) {
      queueFrame(locationId, stamped, priority);
      return { kind: 'queued' };
    }
    throw err;
  }
}

export function useSendFrame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { locationId: string; frame: LightboardFrame; priority: Priority }) =>
      sendFrame(v.locationId, v.frame, v.priority),
    onSettled: () => qc.invalidateQueries({ queryKey: [BOARD_KEY] }),
  });
}

/** A readable sentence from an API refusal. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } } | undefined;
    return body?.error?.message ?? `The server said ${err.status}.`;
  }
  return 'Something went wrong. Try again.';
}

export function useHistory(locationId: string | null, date?: string) {
  return useOfficeQuery({
    queryKey: [BOARD_KEY, 'history', locationId, date ?? 'today'],
    enabled: locationId !== null,
    queryFn: (_client, signal) =>
      clientFor(locationId ?? '').request(
        `/lightboard/history?limit=60${date ? `&date=${date}` : ''}`,
        { schema: LightboardHistorySchema, signal },
      ),
  });
}

const SettingsResponseSchema = z.object({
  settings: LightboardSettingsSchema,
  buttons: z.array(QuickButtonSchema),
});

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { locationId: string; patch: LightboardSettingsPatch }) =>
      clientFor(v.locationId).request('/lightboard/settings', {
        method: 'PUT',
        body: v.patch,
        schema: SettingsResponseSchema,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [BOARD_KEY] }),
  });
}

const OkSchema = z.object({ ok: z.literal(true) });

/**
 * Heartbeat while the board is open, so colleagues see who is watching
 * (Pulse Status online list). Says goodbye on close.
 */
export function usePresence(
  locationIds: readonly string[],
  operatoryId: string | null,
  enabled: boolean,
) {
  const key = locationIds.join(',');
  useEffect(() => {
    if (!enabled || locationIds.length === 0) return undefined;
    const ids = key.split(',');
    const beat = () => {
      for (const id of ids) {
        void clientFor(id)
          .request('/lightboard/presence', {
            method: 'POST',
            body: { operatoryId: operatoryId && ids.length === 1 ? operatoryId : null },
            schema: OkSchema,
          })
          .catch(() => undefined);
      }
    };
    beat();
    const t = setInterval(beat, 30_000);
    return () => {
      clearInterval(t);
      for (const id of ids) {
        void clientFor(id)
          .request('/lightboard/presence', { method: 'DELETE', schema: OkSchema })
          .catch(() => undefined);
      }
    };
    // `locationIds` is folded into `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, operatoryId, enabled]);
}
